"use client";

import Link from "next/link";
import {
  Search,
  TrendingUp,
  Radio,
  Link2,
  Mic2,
} from "lucide-react";
import ServicesCarousel from "@/components/site/ServicesCarousel";
import NetworkConnections from "@/components/site/NetworkConnections";

const featuredCategories = [
  {
    name: "Ferramentas & Software",
    icon: <TrendingUp className="mb-3 h-6 w-6 text-[#5b5cff]" />,
  },
  {
    name: "Monitoramento",
    icon: <Radio className="mb-3 h-6 w-6 text-[#22d3ee]" />,
  },
  {
    name: "Distribuição",
    icon: <Link2 className="mb-3 h-6 w-6 text-[#7c3aed]" />,
  },
  {
    name: "Gestão & Shows",
    icon: <Mic2 className="mb-3 h-6 w-6 text-pink-500" />,
  },
];

const featuredServices = [
  {
    title: "Musitrack Pro",
    vendor: "TrinitMusic Oficial",
    description:
      "Monitoramento de execução em rádios em tempo real. Saiba exatamente onde sua música está tocando no país.",
    price: "R$ 147",
    priceNote: "/mês",
    oldPrice: "R$ 297,00",
    buttonLabel: "Assinar",
    rating: "5.0",
    icon: <Radio className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-indigo-900 to-slate-900",
    vendorOfficial: true,
    accentPrice: true,
  },
  {
    title: "Smart Links & Pré-Save",
    vendor: "TrinitMusic Oficial",
    description:
      "Plataforma de links curtos, pré-saves integrados e captura de audiência para os seus lançamentos.",
    price: "Grátis",
    priceNote: " para testar",
    buttonLabel: "Acessar",
    rating: "4.9",
    icon: <Link2 className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-zinc-800 to-black",
    vendorOfficial: true,
  },
  {
    title: "Relações com a Imprensa",
    vendor: "TrinitMusic Partners",
    description:
      "Desenvolvemos estratégias alinhadas para fortalecer sua imagem e ampliar sua presença na mídia.",
    price: "Sob consulta",
    buttonLabel: "Solicitar",
    rating: "4.9",
    icon: <Mic2 className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-cyan-900 to-slate-900",
  },
  {
    title: "Divulgação em Rádio",
    vendor: "Marco Cavalari",
    description:
      "Planejamento e execução de divulgação focada em rádio, com estratégia comercial e acompanhamento.",
    price: "Sob consulta",
    buttonLabel: "Conhecer",
    rating: "4.8",
    icon: <Radio className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-blue-900 to-slate-900",
    vendorOfficial: true,
  },
  {
    title: "Consultoria Estratégica",
    vendor: "TrinitMusic Partners",
    description:
      "Consultoria para posicionamento artístico, lançamentos, planejamento de carreira e estrutura comercial.",
    price: "R$ 790",
    priceNote: "/sessão",
    buttonLabel: "Agendar",
    rating: "5.0",
    icon: <TrendingUp className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-emerald-900 to-slate-900",
  },
  {
    title: "Distribuição Digital",
    vendor: "TrinitMusic Oficial",
    description:
      "Publique seus lançamentos nas principais plataformas com gestão organizada e acompanhamento centralizado.",
    price: "Sob consulta",
    buttonLabel: "Conhecer",
    rating: "4.9",
    icon: <Link2 className="absolute left-4 top-4 h-10 w-10 text-white/50" />,
    heroClass: "from-fuchsia-900 to-slate-900",
    vendorOfficial: true,
  },
];

export default function MarketplaceHome() {
  return (
    <main className="platform-text min-h-screen overflow-hidden pb-20 bg-transparent">
      <section className="relative overflow-hidden pb-24 pt-20 lg:pb-32 lg:pt-32">
        <NetworkConnections />

        <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_center,rgba(7,11,20,0.06)_0%,rgba(7,11,20,0.22)_36%,rgba(7,11,20,0.58)_68%,rgba(7,11,20,0.84)_100%)]" />

        <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-10">
          <div
            className="mx-auto mb-6 inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-medium backdrop-blur-md"
            style={{
              borderColor: "var(--platform-border, rgba(255,255,255,0.1))",
              background: "rgba(255,255,255,0.05)",
              color: "var(--platform-text-secondary, rgba(248,250,252,0.8))",
            }}
          >
            <span className="mr-2 flex h-2 w-2 rounded-full bg-[#22d3ee]" />
            O marketplace de serviços da indústria musical
          </div>

          <h1 className="mx-auto max-w-4xl text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-7xl">
            Evolua sua carreira com a{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, var(--platform-primary, #5b5cff), var(--platform-accent, #22d3ee))",
              }}
            >
              estrutura certa.
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-lg text-zinc-400">
            Contrate especialistas, ferramentas e serviços para acelerar sua operação.
            Do monitoramento de rádio à distribuição global, tudo em um só lugar.
          </p>

          <div className="relative mx-auto mt-10 max-w-2xl">
            <div
              className="flex items-center rounded-2xl border bg-white/5 p-2 shadow-2xl backdrop-blur-xl"
              style={{ borderColor: "rgba(255,255,255,0.15)" }}
            >
              <div className="flex w-full items-center pl-4">
                <Search className="h-5 w-5 text-zinc-400" />
                <input
                  type="text"
                  className="w-full bg-transparent px-4 py-3 text-white outline-none placeholder:text-zinc-500"
                  placeholder="Ex: Monitoramento de Rádio, Relações com a Imprensa, Consultoria..."
                />
              </div>

              <button
                className="rounded-xl px-6 py-3 font-semibold text-white transition-all hover:opacity-90"
                style={{ background: "var(--platform-primary, #5b5cff)" }}
              >
                Buscar
              </button>
            </div>

            <div className="mt-4 flex flex-wrap justify-center gap-2 text-sm text-zinc-400">
              <span>Buscas populares:</span>
              <Link
                href="#"
                className="underline decoration-zinc-700 underline-offset-4 hover:text-white"
              >
                Musitrack
              </Link>
              <Link
                href="#"
                className="underline decoration-zinc-700 underline-offset-4 hover:text-white"
              >
                Divulgação em Rádio
              </Link>
              <Link
                href="#"
                className="underline decoration-zinc-700 underline-offset-4 hover:text-white"
              >
                Consultoria
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <h2 className="mb-6 text-xl font-semibold text-white">
          Explore por Categoria
        </h2>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {featuredCategories.map((cat, i) => (
            <Link
              href="#"
              key={i}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-6 transition-all hover:-translate-y-1 hover:bg-white/10"
            >
              {cat.icon}
              <h3 className="font-medium text-white">{cat.name}</h3>
            </Link>
          ))}
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-white">
              Serviços em destaque
            </h2>
            <p className="mt-2 text-sm text-zinc-400">
              Uma seleção de soluções para ilustrar o ecossistema da plataforma e apresentar possibilidades para artistas e parceiros.
            </p>
          </div>

          <Link
            href="#"
            className="text-sm font-medium text-[#22d3ee] hover:underline"
          >
            Ver todos
          </Link>
        </div>

        <ServicesCarousel items={featuredServices} />
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-transparent p-8 text-center backdrop-blur-sm md:p-12">
          <h2 className="mb-4 text-3xl font-bold text-white">
            Você presta serviços para a música?
          </h2>
          <p className="mx-auto mb-8 max-w-2xl text-lg text-zinc-400">
            Abra sua loja na TrinitMusic e venda suas ferramentas, consultorias
            ou serviços diretamente para artistas, escritórios e parceiros do mercado.
          </p>
          <button
            className="rounded-full px-8 py-4 font-bold text-white transition-all hover:scale-105"
            style={{
              background:
                "linear-gradient(135deg, var(--platform-primary, #5b5cff) 0%, var(--platform-accent, #22d3ee) 100%)",
            }}
          >
            Cadastrar como Parceiro
          </button>
        </div>
      </section>
    </main>
  );
}